import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAnalyticsParams, IAppAnalytics } from '~/types/analytic'

import Overview from './Overview'
import BusinessReport from './Reporting'
import Sandbox from './Sandbox'

import { getBusinessReportAnalytics } from '~/api/application-analytics'
import Banner from '~/components/Banner'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS, DEVICE_TYPE, LOCAL_STORAGE } from '~/const'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { analyticsKeys, useFetchAnalyticsCacheStateQuery } from '~/queries/analytics'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { getHeaderParams } from '~/utils/application'

import './styles.scss'

const Router = ({ appId }: { appId: string }): ReactElement => {
  const { deviceType, isLoading } = useAppSummary()

  const { state } = useAnalyticsContext()

  const graphConfigAuthorized = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.BUSINESS_REPORTS.READ])

  const analyticsViewProps = { appId, isAppLoading: isLoading }

  const appKeys = state.filters.appKey

  return (
    <Routes>
      <Route
        element={
          <BusinessReport
            {...analyticsViewProps}
            analyticsApiFn={(params: IAnalyticsParams) => getBusinessReportAnalytics<IAppAnalytics>(appId, params)}
          />
        }
        path="legacy"
      />
      <Route
        element={
          graphConfigAuthorized ? (
            <Overview appKeys={appKeys} deviceType={deviceType} kpiField="" {...analyticsViewProps} />
          ) : (
            <Navigate replace to="/unauthorized" />
          )
        }
        path="overview"
      />
      <Route
        element={
          graphConfigAuthorized ? (
            <Sandbox appKeys={appKeys} deviceType={deviceType} kpiField="" {...analyticsViewProps} />
          ) : (
            <Navigate replace to="/unauthorized" />
          )
        }
        path="sandbox"
      />
      <Route element={<Navigate replace to="/404" />} path="*" />
    </Routes>
  )
}

const AnalyticsRouter = (): ReactElement => {
  /** Local state */

  const { pathname } = useLocation()

  const queryClient = useQueryClient()

  const titleParams = useMemo(() => getHeaderParams(pathname), [pathname])

  /** Queries & Mutations */

  const { appKey, deviceType } = useAppSummary()

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { data: analyticsCacheState } = useFetchAnalyticsCacheStateQuery()

  /** Effects */

  useEffect(() => {
    if (analyticsCacheState?.refreshed_at_ts) {
      const lastRefresh = localStorage.getItem(LOCAL_STORAGE.ANALYTICS_LAST_REFRESH)

      if (!lastRefresh || analyticsCacheState.refreshed_at_ts !== +lastRefresh) {
        queryClient.invalidateQueries({ queryKey: analyticsKeys.all })
        localStorage.setItem(LOCAL_STORAGE.ANALYTICS_LAST_REFRESH, `${analyticsCacheState.refreshed_at_ts}`)
      }
    }
  }, [analyticsCacheState, queryClient])

  /** Render */

  return (
    <div className="view-layout analytics-router">
      <ViewTitle className="layout-view-title">
        {titleParams?.title}
        {appSummary?.device_type === DEVICE_TYPE.ANDROID ? (
          <Banner className="title-banner" variant="warning">
            Please note that some data may be incomplete for Android
          </Banner>
        ) : null}
      </ViewTitle>
      {appSummary?.app_id ? <Router appId={appSummary?.app_id} /> : null}
    </div>
  )
}

export default AnalyticsRouter
